import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import {
    NavActionButtonsComponent,
    NavBasicSlidesRowComponent,
    NavBasicToggleComponent,
    NavExtensionSlidesRowComponent,
    NavExtensionToggleComponent,
} from 'shared/classroom/preview-screen/ui-beamer-nav';
import { fromToolbar, ToolbarActions } from 'shared/classroom/toolbar/data-access';
import { FloatingToolbarComponent } from 'shared/classroom/toolbar/feature-shell';
import { IconButtonDirective } from 'shared/ui/icon-button';
import { getStoredNavSide, NavSide, updateStoredNavSide } from './beamer-nav-side-storage';
import { NextSlidesPreviewFacade } from './next-slides-preview.facade';

/**
 * Beamer nav with the "frozen glass" nav toolbar (PCP beamer), incl. the floating drawing toolbar.
 * The rows, toggles and action buttons are the shared parts of `shared/classroom/preview-screen/ui-beamer-nav`,
 * wired to `NextSlidesPreviewFacade`.
 */
@Component({
    selector: 'pcp-next-slides-preview-glassmorphic',
    templateUrl: './next-slides-preview-glassmorphic.component.html',
    styleUrls: ['./next-slides-preview-glassmorphic.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [NextSlidesPreviewFacade],
    host: {
        '[class.nav-left]': "side() === 'left'",
    },
    imports: [
        NgTemplateOutlet,
        MatIconButton,
        MatIcon,
        MatTooltip,
        TranslocoPipe,
        IconButtonDirective,
        FloatingToolbarComponent,
        NavActionButtonsComponent,
        NavBasicSlidesRowComponent,
        NavBasicToggleComponent,
        NavExtensionSlidesRowComponent,
        NavExtensionToggleComponent,
    ],
})
export class NextSlidesPreviewGlassmorphicComponent {
    protected readonly nav = inject(NextSlidesPreviewFacade);
    private readonly store = inject(Store);

    readonly showFloatingToolbar = input.required<boolean>();
    /** The floating toolbar is open and visible (computed by the parent `app-slide-preview`). */
    readonly isFloatingToolbarOpened = input(false);

    /** Screen side of the nav; the "switch" button flips it. Kept in localStorage (this beamer only). */
    protected readonly side = signal<NavSide>(getStoredNavSide());

    /**
     * Saved place of the floating toolbar (after the user dragged it), or null.
     * null  → the toolbar sits in its default place: a CSS grid cell next to its open button (no JS).
     * saved → it sits in the fixed layer, at this distance from the window's bottom-right corner.
     */
    protected readonly savedToolbarPosition = toSignal(
        this.store.select(fromToolbar.selectToolbarPosition), { initialValue: null });

    protected readonly hasSomeActionButton = computed(
        () => this.showFloatingToolbar() || this.nav.hasActionIndicators(),
    );

    protected toggleSide() {
        const side: NavSide = this.side() === 'right' ? 'left' : 'right';
        this.side.set(side);
        updateStoredNavSide(side);
    }

    /** The drawing-toolbar button is a toggle: it opens the floating toolbar, or closes it when it is open. */
    protected onToggleFloatingToolbar() {
        this.store.dispatch(
            this.isFloatingToolbarOpened() ? ToolbarActions.closeFloatingToolbar() : ToolbarActions.openFloatingToolbar()
        );
    }
}
